<template>
  <qtm-dialog-card
    :model-value="modelValue"
    :persistent="loading"
    :title="message"
    width="474"
  >
    <template v-if="resetOoo">
      <div class="text-dark-grey mb-6">
        This will clear the Out Of Office status for this contact.
      </div>
    </template>
    <template v-else>
      <div class="text-dark-grey mb-6">
        If you don’t choose a date, the OOO status will be cleared after 1 week.
      </div>
      <qtm-input-label label="Set to OOO until...">
        <date-picker v-model="endDate" clearable close-on-click hide-details="auto"/>
      </qtm-input-label>
    </template>
    <template v-slot:actions>
      <v-spacer />
      <qtm-btn :loading="loading" size="x-large" tertiary @click="$emit('update:model-value', false)">
        Cancel
      </qtm-btn>
      <qtm-btn :loading="loading" size="x-large" @click="save">
        {{ message }}
      </qtm-btn>
    </template>
  </qtm-dialog-card>
</template>

<script setup lang='ts'>
import { type Contact, DateTime } from '@quotetome/materials-api'

export interface Props {
  contact?: Contact | null
  email?: string
  modelValue?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits(['update:model-value', 'update:ooo-date-started'])

const endDate = ref<DateTime | null>(null)
const loading = ref(false)

const resetOoo = computed(() => !!props.contact?.ooo_date_started)
const message = computed(() => `${resetOoo.value ? 'Uns' : 'S'}et as Out Of Office`)

const { $api, $error, $toast } = useNuxtApp()
const save = async () => {
  loading.value = true
  try {
    if (resetOoo.value) {
      await $api.v1.vendors.updateContact(props.contact!.id, {
        ooo_date_ended: null,
        ooo_date_started: null,
      })
      emit('update:ooo-date-started', null)
    }
    else {
      const today = new DateTime()
        .withHour(0)
        .withMinute(0)
        .withSecond(0)
        .withMillisecond(0)

      await $api.v1.vendors.setOutOfOffice({
        email: props.email,
        ooo_date_ended: endDate.value?.unix() ?? today.add(7, 'day').unix(),
        ooo_date_started: today.unix(),
      })
      emit('update:ooo-date-started', today.unix())
    }

    $toast.success(`Contact was successfully ${resetOoo.value ? 'uns' : 's'}et as Out Of Office.`)
    emit('update:model-value', false)
  }
  catch (error) {
    $error.report(error)
  }
  finally {
    loading.value = false
  }
}
</script>
